import EntityMember from "_shared/entity-members/entity-member";
import type Individual from "marketplace/individuals/individual";
import { isKnownToLackCredentials } from "marketplace/individuals/profiles/professional-credentials";

/**
 * The members a team template would add to a vendor team, split into the ones who may join and the
 * names of the ones whose profile lists no professional credentials (#5800).
 *
 * The member pickers decline such a person, but a template named its people before the rule existed,
 * or before somebody emptied their profile, so it can still carry them. Selecting the template used to
 * add them anyway, and the save then failed with nothing on the roster to say why (#6613). The API
 * refuses only the people a change adds, so somebody already on the team is not declined here either.
 *
 * A person whose individual has not loaded, or arrives contact-only, is let through: this cannot see
 * their credentials, and the API still decides when the team is saved.
 */
export function splitTemplateMembersByCredentials(params: {
  templateMembers: readonly EntityMember[];
  individuals: readonly Individual[];
  alreadyOnTeam: readonly (EntityMember | null | undefined)[];
}): { joining: EntityMember[]; declinedFullNames: string[] } {
  const joining: EntityMember[] = [];
  const declinedFullNames: string[] = [];
  for (const member of params.templateMembers) {
    const individual = params.individuals.find((candidate) => candidate.userId.isEqualTo(member.userId));
    const isAlreadyOnTeam = params.alreadyOnTeam.some((onTeam) => onTeam?.userId?.isEqualTo(member.userId));
    if (individual && !isAlreadyOnTeam && isKnownToLackCredentials(individual)) {
      declinedFullNames.push(individual.fullName);
      continue;
    }
    joining.push(member);
  }
  return { joining, declinedFullNames };
}

/**
 * Says which of a template's people were left off the team, and why, in the words the pickers use.
 */
export function getTemplateMembersDeclinedMessage(templateName: string, fullNames: readonly string[]): string {
  if (fullNames.length === 1) {
    return `${fullNames[0]} was not added from "${templateName}". They can't join a team until their profile lists professional credentials.`;
  }
  const names = `${fullNames.slice(0, -1).join(", ")} and ${fullNames[fullNames.length - 1]}`;
  return `${names} were not added from "${templateName}". They can't join a team until their profiles list professional credentials.`;
}
